/**
 * Pictogram — original SonSpeak animated gesture pictograms.
 *
 * One friendly character ("Buddy"), drawn once, posed per gesture by
 * rotating the arm/head groups around their pivots. Tapping a tile adds
 * the `is-playing` class and the gesture animates (wave, arm out for
 * stop, both arms up for help…). Motion is pure CSS — see demoboard.css —
 * and is disabled under prefers-reduced-motion.
 *
 * These are original works authored in-repo (child-safety asset rule:
 * every child-facing image is human-reviewable; nothing is fetched).
 */

const INK = "var(--ink, #1F1A17)";
const SKIN = "#FFC66E";
const SHIRT = "#E2574C";
const CHEEK = "#F38BA8";

/**
 * Arm — a rounded capsule + hand, drawn hanging straight down from the
 * shoulder pivot at (0,0). Poses rotate the whole group; the wrist can
 * carry its own class for hand-only motion.
 */
function Arm({ side, big = false }) {
  return (
    <g className={`picto-arm picto-arm-${side}`}>
      <rect x="-5.5" y="-2" width="11" height="30" rx="5.5" fill={SHIRT} stroke={INK} strokeWidth="3" />
      <g className="picto-hand">
        <circle cx="0" cy="32" r={big ? 9 : 7.5} fill={SKIN} stroke={INK} strokeWidth="3" />
        {big && (
          <g stroke={INK} strokeWidth="2" strokeLinecap="round">
            <line x1="-4" y1="25" x2="-4" y2="28" />
            <line x1="0" y1="24" x2="0" y2="27" />
            <line x1="4" y1="25" x2="4" y2="28" />
          </g>
        )}
      </g>
    </g>
  );
}

function Head() {
  return (
    <g className="picto-head">
      <circle cx="60" cy="30" r="19" fill={SKIN} stroke={INK} strokeWidth="3.5" />
      <circle cx="53.5" cy="27" r="2.4" fill={INK} />
      <circle cx="66.5" cy="27" r="2.4" fill={INK} />
      <circle cx="49" cy="34" r="3" fill={CHEEK} opacity="0.45" />
      <circle cx="71" cy="34" r="3" fill={CHEEK} opacity="0.45" />
      <path d="M52 36 Q60 43 68 36" fill="none" stroke={INK} strokeWidth="3" strokeLinecap="round" />
    </g>
  );
}

function Torso() {
  return (
    <path
      d="M42 55 Q42 49 49 49 L71 49 Q78 49 78 55 L78 92 L42 92 Z"
      fill={SHIRT}
      stroke={INK}
      strokeWidth="3.5"
    />
  );
}

/** Badge — the familiar ✓ / ✗ marks kids already know from every board. */
function Badge({ kind }) {
  if (kind === "check") {
    return (
      <g className="picto-badge">
        <circle cx="97" cy="22" r="14" fill="#DEF7DE" stroke={INK} strokeWidth="3" />
        <path d="M90 22 L95 28 L104 16" fill="none" stroke="#2E7D32" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    );
  }
  return (
    <g className="picto-badge">
      <circle cx="97" cy="22" r="14" fill="#FCE4E4" stroke={INK} strokeWidth="3" />
      <path d="M91 16 L103 28 M103 16 L91 28" fill="none" stroke="#C62828" strokeWidth="4.5" strokeLinecap="round" />
    </g>
  );
}

/**
 * Every gesture renders the same character with different pivot groups.
 * Shoulder pivots: left (44,57), right (76,57). Class names key the CSS
 * poses + animations (demoboard.css).
 */
export default function Pictogram({ name, playing = false }) {
  const cls = `picto picto-${name}${playing ? " is-playing" : ""}`;
  const isBig = name === "stop"; // stop gets the big open palm
  return (
    <svg
      viewBox="0 0 120 100"
      className={cls}
      aria-hidden="true"
      focusable="false"
    >
      {/* arms behind the torso for raised poses, in front for crossed */}
      <g transform="translate(44 57)" className="picto-shoulder picto-shoulder-l">
        <Arm side="l" />
      </g>
      <g transform="translate(76 57)" className="picto-shoulder picto-shoulder-r">
        <Arm side="r" big={isBig} />
      </g>
      <Torso />
      <Head />
      {name === "yes" && <Badge kind="check" />}
      {name === "no" && <Badge kind="x" />}
    </svg>
  );
}

/** Gesture ids the CSS knows how to pose/animate. */
export const PICTOGRAM_NAMES = [
  "hi",
  "bye",
  "stop",
  "help",
  "more",
  "yes",
  "no",
  "want",
  "alldone",
  "go",
  "hug",
];
